/* eslint-disable sonarjs/no-identical-functions */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import {
  ShortFallEmployee,
  ShortFallProps,
  ShortFallSliceState,
} from '../../types/ShortfallTrackerReport/shortfallTrackerTypes'
import { RootState } from '../../stateStore'
import { LoadingState, ValidationError } from '../../types/commonTypes'
import { shortFallApi } from '../../middleware/api/ShortFall/shortFallApi'
import { ApiLoadingState } from '../../middleware/api/apiList'

//INITIAL VALUE OF THE GLOBAL OBJECTS
const initialShortFallObject: ShortFallProps = {
  shortlisted: false,
  startDate: '',
  endDate: '',
  dateSelection: 'Last week',
  billingType: 'All',
  search: '',
  endIndex: 1000,
  startIndex: 0,
}

const initialShortFallFormState: ShortFallSliceState = {
  shortFallEmployees: [],
  isLoading: ApiLoadingState.idle,
  isLoadingForCount: ApiLoadingState.idle,
  listSize: 0,
  shortFallObject: initialShortFallObject,
  fromSearch: false,
  shortFallEmployeesAll: [],
}

///THUNKS
const getShortFallReport = createAsyncThunk(
  'timeInOffice/getShortFallReport',
  async (props: ShortFallProps, thunkApi) => {
    try {
      return await shortFallApi.getShortFallList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getShortFallReportForCount = createAsyncThunk(
  'timeInOffice/getShortFallReportCount',
  async (props: ShortFallProps, thunkApi) => {
    try {
      return await shortFallApi.getShortFallList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const exportShortFallList = createAsyncThunk(
  'timeInOffice/exportShortFallList',
  async (props: ShortFallProps, thunkApi) => {
    try {
      return await shortFallApi.exportShortFallList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
////END OF THUNKS

const shortFallSlice = createSlice({
  name: 'shortFallSlice',
  initialState: initialShortFallFormState,
  reducers: {
    setShortFallObject: (state, action) => {
      state.shortFallObject = action.payload
    },
    setClearShortFallTable: (state) => {
      state.shortFallEmployees = []
      state.shortFallObject = initialShortFallObject
      state.listSize = 0
    },
    //SPREAD THE OBJECT SO THAT WHEN WE CHANGE THE VALUE OF ONE, WE CAN STILL GET THE OTHER VALUES
    setDateSelection: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        dateSelection: action.payload,
      }
    },
    setShortListed: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        shortlisted: action.payload,
      }
    },
    setBillingType: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        billingType: action.payload,
      }
    },
    setSearch: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        search: action.payload,
      }
    },
    setStartDate: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        startDate: action.payload,
      }
    },
    setEndDate: (state, action) => {
      state.shortFallObject = {
        ...state.shortFallObject,
        endDate: action.payload,
      }
    },
    setFromSearch: (state, action) => {
      state.fromSearch = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getShortFallReport.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getShortFallReport.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
      })
      .addCase(getShortFallReport.fulfilled, (state, action) => {
        state.shortFallEmployees = action.payload.attendanceReportList
        state.isLoading = ApiLoadingState.succeeded
        state.listSize = action.payload.size
      })
      .addCase(getShortFallReportForCount.pending, (state) => {
        state.isLoadingForCount = ApiLoadingState.loading
      })
      .addCase(getShortFallReportForCount.rejected, (state) => {
        state.isLoadingForCount = ApiLoadingState.failed
      })
      .addCase(getShortFallReportForCount.fulfilled, (state, action) => {
        state.shortFallEmployeesAll = action.payload.attendanceReportList
        state.isLoadingForCount = ApiLoadingState.succeeded
        state.listSize = action.payload.size
      })
  },
})

const getIsLoading = (state: RootState): LoadingState =>
  state.shortFall.isLoading

const getShortFallList = (state: RootState): ShortFallEmployee[] =>
  state.shortFall.shortFallEmployees

const getShortFallListAll = (state: RootState): ShortFallEmployee[] =>
  state.shortFall.shortFallEmployeesAll

const getShortFallObject = (state: RootState): ShortFallProps =>
  state.shortFall.shortFallObject

const getListSizeAll = (state: RootState): number => state.shortFall.listSize

const getFromSearch = (state: RootState): boolean => state.shortFall.fromSearch

const getIsLoadingCount = (state: RootState): LoadingState =>
  state.shortFall.isLoadingForCount

export const shortFallThunk = {
  getShortFallReport,
  exportShortFallList,
  getShortFallReportForCount,
}

export const shortFallSelectors = {
  getIsLoading,
  getShortFallList,
  getShortFallObject,
  getListSizeAll,
  getFromSearch,
  getShortFallListAll,
  getIsLoadingCount,
}

export const shortFallService = {
  ...shortFallThunk,
  actions: shortFallSlice.actions,
  selectors: shortFallSelectors,
}

export default shortFallSlice.reducer
